import { Color3 } from '@babylonjs/core';
import { Block, BlockDefinition } from './Block';

export class BlockRegistry {
  private static instance: BlockRegistry | null = null;
  private blocks: Map<number, BlockDefinition> = new Map();

  private constructor() {
    this.registerDefaultBlocks();
  }

  public static getInstance(): BlockRegistry {
    if (!BlockRegistry.instance) {
      BlockRegistry.instance = new BlockRegistry();
    }
    return BlockRegistry.instance;
  }

  private registerDefaultBlocks(): void {
    // Air (invisible)
    this.register({
      id: Block.AIR,
      name: 'air',
      displayName: 'Ar',
      color: new Color3(0, 0, 0),
      transparent: true,
      solid: false,
    });

    // Grass
    this.register({
      id: Block.GRASS,
      name: 'grass',
      displayName: 'Grama',
      color: new Color3(0.3, 0.7, 0.2),
      transparent: false,
      solid: true,
    });

    // Dirt
    this.register({
      id: Block.DIRT,
      name: 'dirt',
      displayName: 'Terra',
      color: new Color3(0.55, 0.35, 0.2),
      transparent: false,
      solid: true,
    });

    // Stone
    this.register({
      id: Block.STONE,
      name: 'stone',
      displayName: 'Pedra',
      color: new Color3(0.5, 0.5, 0.5),
      transparent: false,
      solid: true,
    });

    // Wood (log)
    this.register({
      id: Block.WOOD,
      name: 'wood',
      displayName: 'Madeira',
      color: new Color3(0.45, 0.3, 0.15),
      transparent: false,
      solid: true,
    });

    // Leaves
    this.register({
      id: Block.LEAVES,
      name: 'leaves',
      displayName: 'Folhas',
      color: new Color3(0.2, 0.5, 0.15),
      transparent: true,
      solid: true,
    });

    // Sand
    this.register({
      id: Block.SAND,
      name: 'sand',
      displayName: 'Areia',
      color: new Color3(0.9, 0.85, 0.6),
      transparent: false,
      solid: true,
    });

    // Water
    this.register({
      id: Block.WATER,
      name: 'water',
      displayName: 'Água',
      color: new Color3(0.2, 0.4, 0.8),
      transparent: true,
      solid: false,
    });

    // Brick
    this.register({
      id: Block.BRICK,
      name: 'brick',
      displayName: 'Tijolo',
      color: new Color3(0.7, 0.35, 0.3),
      transparent: false,
      solid: true,
    });

    // Glass
    this.register({
      id: Block.GLASS,
      name: 'glass',
      displayName: 'Vidro',
      color: new Color3(0.8, 0.9, 0.95),
      transparent: true,
      solid: true,
    });

    // Cobblestone
    this.register({
      id: Block.COBBLESTONE,
      name: 'cobblestone',
      displayName: 'Pedregulho',
      color: new Color3(0.4, 0.4, 0.42),
      transparent: false,
      solid: true,
    });

    // Planks
    this.register({
      id: Block.PLANKS,
      name: 'planks',
      displayName: 'Tábuas',
      color: new Color3(0.7, 0.55, 0.3),
      transparent: false,
      solid: true,
    });

    // Gold
    this.register({
      id: Block.GOLD,
      name: 'gold',
      displayName: 'Ouro',
      color: new Color3(1.0, 0.85, 0.2),
      transparent: false,
      solid: true,
    });

    // Iron
    this.register({
      id: Block.IRON,
      name: 'iron',
      displayName: 'Ferro',
      color: new Color3(0.75, 0.75, 0.78),
      transparent: false,
      solid: true,
    });

    // Diamond
    this.register({
      id: Block.DIAMOND,
      name: 'diamond',
      displayName: 'Diamante',
      color: new Color3(0.3, 0.85, 0.9),
      transparent: false,
      solid: true,
    });

    // Bedrock
    this.register({
      id: Block.BEDROCK,
      name: 'bedrock',
      displayName: 'Rocha Matriz',
      color: new Color3(0.2, 0.2, 0.2),
      transparent: false,
      solid: true,
    });
  }

  public register(definition: BlockDefinition): void {
    this.blocks.set(definition.id, definition);
  }

  public get(id: number): BlockDefinition | undefined {
    return this.blocks.get(id);
  }

  public getByName(name: string): BlockDefinition | undefined {
    for (const block of this.blocks.values()) {
      if (block.name === name) {
        return block;
      }
    }
    return undefined;
  }

  public getAll(): BlockDefinition[] {
    return Array.from(this.blocks.values());
  }

  public getSolidBlocks(): BlockDefinition[] {
    return this.getAll().filter(b => b.solid && b.id !== Block.AIR);
  }
}
